// Markdown.tsx — a tiny, dependency-free renderer for the constrained markdown
// our lesson formatter produces: ## / ### headings, **bold**, and - / 1. lists.
// Anything else is a paragraph. Styling matches the editorial reading voice.
import React from 'react';

type Block =
  | { kind: 'h2' | 'h3' | 'p'; text: string }
  | { kind: 'ul' | 'ol'; items: string[] };

function parse(md: string): Block[] {
  const lines = md.replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];
  let para: string[] = [];
  let list: { kind: 'ul' | 'ol'; items: string[] } | null = null;

  const flushPara = () => {
    if (para.length) { blocks.push({ kind: 'p', text: para.join(' ') }); para = []; }
  };
  const flushList = () => {
    if (list) { blocks.push(list); list = null; }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushPara(); flushList(); continue; }

    const h = /^(#{2,3})\s+(.*)$/.exec(line);
    const ul = /^[-*]\s+(.*)$/.exec(line);
    const ol = /^\d+[.)]\s+(.*)$/.exec(line);

    if (h) {
      flushPara(); flushList();
      blocks.push({ kind: h[1].length === 2 ? 'h2' : 'h3', text: h[2] });
    } else if (ul) {
      flushPara();
      if (!list || list.kind !== 'ul') { flushList(); list = { kind: 'ul', items: [] }; }
      list.items.push(ul[1]);
    } else if (ol) {
      flushPara();
      if (!list || list.kind !== 'ol') { flushList(); list = { kind: 'ol', items: [] }; }
      list.items.push(ol[1]);
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara(); flushList();
  return blocks;
}

// Inline **bold** → <strong>.
function inline(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    const m = /^\*\*([^*]+)\*\*$/.exec(p);
    return m ? <strong key={i} style={{ fontWeight: 700, color: 'var(--ink)' }}>{m[1]}</strong> : <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

const headingBase: React.CSSProperties = {
  fontFamily: 'Fraunces, Georgia, serif',
  color: 'var(--ink)',
  lineHeight: 1.25,
};

export function Markdown({ source, scale = 1 }: { source: string; scale?: number }) {
  const blocks = React.useMemo(() => parse(source), [source]);
  const s = (px: number) => Math.round(px * scale * 10) / 10;
  const itemStyle: React.CSSProperties = { fontFamily: 'Inter, system-ui, sans-serif', fontSize: s(16), lineHeight: 1.66, color: 'var(--ink)' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {blocks.map((b, i) => {
        switch (b.kind) {
          case 'h2':
            return <h2 key={i} style={{ ...headingBase, fontSize: s(20.5), fontWeight: 600, margin: i ? '12px 0 -2px' : 0 }}>{inline(b.text)}</h2>;
          case 'h3':
            return <h3 key={i} style={{ ...headingBase, fontSize: s(17), fontWeight: 600, margin: i ? '8px 0 -4px' : 0 }}>{inline(b.text)}</h3>;
          case 'ul':
            // refined bullets — a small amber dot, generous spacing, hanging indent
            return (
              <ul key={i} style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: s(11) }}>
                {b.items.map((it, j) => (
                  <li key={j} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', ...itemStyle }}>
                    <span aria-hidden="true" style={{ flexShrink: 0, width: 6, height: 6, borderRadius: 999, background: 'var(--amber-deep)', marginTop: s(16) * 0.62 }} />
                    <span style={{ flex: 1 }}>{inline(it)}</span>
                  </li>
                ))}
              </ul>
            );
          case 'ol':
            return (
              <ol key={i} style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: s(11) }}>
                {b.items.map((it, j) => (
                  <li key={j} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', ...itemStyle }}>
                    <span aria-hidden="true" style={{ flexShrink: 0, minWidth: 16, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, color: 'var(--amber-deep)' }}>{j + 1}.</span>
                    <span style={{ flex: 1 }}>{inline(it)}</span>
                  </li>
                ))}
              </ol>
            );
          default:
            return <p key={i} style={{ margin: 0, ...itemStyle }}>{inline(b.text)}</p>;
        }
      })}
    </div>
  );
}
